import { PageId } from "./pages";

export enum CountryCode {
  DE = "DE",
  ES = "ES",
  FR = "FR",
  IT = "IT",
  PT = "PT",
  FI = "FI",
  GR = "GR",
}

export type Locale = "de" | "el" | "es" | "fi" | "fr" | "it" | "pt";
export const availableLocales: Locale[] = ["de", "el", "es", "fi", "fr", "it", "pt"];

export interface ContactMethodElement {
  destination: string;
  messageToShow?: string;
  phoneNumberToShow?: string;
  type: string;
}

export interface PhoneNumberLengthValidation {
  min: number;
  max: number;
}

export interface CountrySubsetConfiguration {
  countryCode: CountryCode;
  defaultLocale: Locale | string;
  name: string;
}

export interface CountryConfiguration extends CountrySubsetConfiguration {
  allowChangePolicyStartDate: boolean;
  chatLink: boolean;
  contactMethods: ContactMethodElement[];
  euFederLogos: boolean;
  exceptionStartingNumbersForWhatsApp: string[];
  fakeChipNumber: boolean;
  glAddon: boolean;
  hasComplianceErrorMessage: boolean;
  hasDescriptionInAccessPage: boolean;
  hasTattooAnswer: boolean;
  idDocumentRequired: boolean;
  invalidStartingNumbersForWhatsApp: string[];
  listTaxes: boolean;
  multiplePetsDiscount: number;
  pagesWithChatLink: PageId[];
  paymentMethods: string[];
  phoneNumberLength: PhoneNumberLengthValidation;
  phoneNumberPrefix: string;
  preContractNotificationIsRequired: boolean;
  sanitizePhoneNumber: boolean;
  selectableProductsDisclaimer: boolean;
  smallPrintPages: boolean;
  preQuotesZipcodeQuestion: boolean;
  twoPartnersQuotes: boolean;
}

type CountryOverrides = Partial<Omit<CountryConfiguration, keyof CountrySubsetConfiguration>>;

const baseChatPages = [
  PageId.uncovered,
  PageId.quotes,
  PageId.petParent,
  PageId.address,
  PageId.access,
  PageId.chip,
  PageId.petFamily,
  PageId.details,
  PageId.landing,
] as const;

const spainAdditionalChatPages = [
  PageId.hasChip,
  PageId.noChip,
  PageId.smallPrint,
  PageId.preExistencesConditions,
  PageId.waitingPeriod,
] as const;

const defaultCountryConfiguration: Omit<
  CountryConfiguration,
  "countryCode" | "defaultLocale" | "name"
> = {
  allowChangePolicyStartDate: true,
  chatLink: false,
  contactMethods: [],
  euFederLogos: false,
  exceptionStartingNumbersForWhatsApp: [],
  fakeChipNumber: true,
  glAddon: false,
  hasComplianceErrorMessage: false,
  hasDescriptionInAccessPage: false,
  hasTattooAnswer: false,
  idDocumentRequired: false,
  invalidStartingNumbersForWhatsApp: [],
  listTaxes: false,
  multiplePetsDiscount: 25,
  pagesWithChatLink: [...baseChatPages],
  paymentMethods: ["card"],
  phoneNumberLength: { min: 9, max: 9 },
  phoneNumberPrefix: "",
  preContractNotificationIsRequired: false,
  sanitizePhoneNumber: false,
  selectableProductsDisclaimer: false,
  smallPrintPages: false,
  preQuotesZipcodeQuestion: false,
  twoPartnersQuotes: false,
};

const spainConfiguration: CountryOverrides = {
  contactMethods: [
    {
      destination: "https://wa.me/message/Q5KWYO5LVFTYK1?utm_term=maincta",
      type: "whatsapp",
      messageToShow: "common.whatsapp_contact.messageToShow",
    },
  ],
  euFederLogos: true,
  fakeChipNumber: false,
  glAddon: true,
  hasComplianceErrorMessage: true,
  idDocumentRequired: true,
  pagesWithChatLink: [
    PageId.uncovered,
    PageId.quotes,
    PageId.hasChip,
    PageId.noChip,
    PageId.petParent,
    PageId.address,
    PageId.access,
    PageId.chip,
    PageId.petFamily,
    PageId.smallPrint,
    PageId.preExistencesConditions,
    PageId.waitingPeriod,
    PageId.details,
    PageId.landing,
  ],
  smallPrintPages: true,
  sanitizePhoneNumber: false,
  selectableProductsDisclaimer: true,
  preQuotesZipcodeQuestion: true,
};

function createCountryConfiguration(
  subsetConfig: CountrySubsetConfiguration,
  overrides: CountryOverrides
): CountryConfiguration {
  return {
    ...defaultCountryConfiguration,
    ...overrides,
    ...subsetConfig,
  };
}

export const countries: Record<CountryCode, CountryConfiguration> = {
  DE: createCountryConfiguration(
    {
      countryCode: CountryCode.DE,
      defaultLocale: "de",
      name: "Deutschland",
    },
    {
      chatLink: true,
      contactMethods: [
        {
          destination: "https://wa.me/message/AL336C7QS7K4G1?utm_term=maincta",
          type: "whatsapp",
          messageToShow: "common.whatsapp_contact.messageToShow",
        },
      ],
      exceptionStartingNumbersForWhatsApp: ["15", "16", "17"],
      hasTattooAnswer: true,
      invalidStartingNumbersForWhatsApp: ["1", "2", "3"],
      listTaxes: true,
      multiplePetsDiscount: 50,
      paymentMethods: ["card", "sepa_debit"],
      phoneNumberLength: { min: 8, max: 12 },
      phoneNumberPrefix: "49",
      sanitizePhoneNumber: true,
    }
  ),
  FR: createCountryConfiguration(
    {
      countryCode: CountryCode.FR,
      defaultLocale: "fr",
      name: "France",
    },
    {
      contactMethods: [
        {
          destination: "https://wa.me/message/UC7OSSJPSYO3L1?utm_term=maincta",
          type: "whatsapp",
          messageToShow: "common.whatsapp_contact.messageToShow",
        },
      ],
      hasDescriptionInAccessPage: true,
      invalidStartingNumbersForWhatsApp: ["1", "2", "3", "4", "5"],
      phoneNumberLength: { min: 9, max: 10 },
      phoneNumberPrefix: "33",
      preContractNotificationIsRequired: true,
      twoPartnersQuotes: true,
    }
  ),
  IT: createCountryConfiguration(
    {
      countryCode: CountryCode.IT,
      defaultLocale: "it",
      name: "Italia",
    },
    {
      contactMethods: [
        {
          destination: "https://wa.me/message/5CBJS2TOGOLWA1?utm_term=maincta",
          type: "whatsapp",
          messageToShow: "common.whatsapp_contact.messageToShow",
        },
      ],
      phoneNumberLength: { min: 6, max: 12 },
      phoneNumberPrefix: "39",
    }
  ),
  PT: createCountryConfiguration(
    {
      countryCode: CountryCode.PT,
      defaultLocale: "pt",
      name: "Portugal",
    },
    {
      contactMethods: [
        {
          destination: "https://wa.me/message/YE6MEOBRSFTYB1?utm_term=maincta",
          type: "whatsapp",
          messageToShow: "common.whatsapp_contact.messageToShow",
        },
      ],
      phoneNumberPrefix: "351",
    }
  ),
  ES: createCountryConfiguration(
    {
      countryCode: CountryCode.ES,
      defaultLocale: "es",
      name: "España",
    },
    {
      contactMethods: [
        {
          destination: "https://wa.me/message/Q5KWYO5LVFTYK1?utm_term=maincta",
          type: "whatsapp",
          messageToShow: "common.whatsapp_contact.messageToShow",
        },
      ],
      euFederLogos: true,
      fakeChipNumber: false,
      glAddon: true,
      hasComplianceErrorMessage: true,
      idDocumentRequired: true,
      pagesWithChatLink: [...baseChatPages, ...spainAdditionalChatPages],
      smallPrintPages: true,
      selectableProductsDisclaimer: true,
      preQuotesZipcodeQuestion: true,
    }
  ),
  FI: createCountryConfiguration(
    {
      countryCode: CountryCode.FI,
      defaultLocale: "fi",
      name: "Finland",
    },
    {
      contactMethods: [
        {
          destination: "https://wa.me/message/NVABCRCQ3JO3M1?utm_term_maincta",
          type: "whatsapp",
          messageToShow: "common.whatsapp_contact.messageToShow",
        },
      ],
      phoneNumberLength: { min: 5, max: 12 },
      phoneNumberPrefix: "358",
    }
  ),
  GR: createCountryConfiguration(
    {
      countryCode: CountryCode.GR,
      defaultLocale: "el",
      name: "Ελλάδα",
    },
    {
      contactMethods: [
        {
          destination: "https://wa.me/message/5CBJS2TOGOLWA1?utm_term=maincta",
          type: "whatsapp",
          messageToShow: "common.whatsapp_contact.messageToShow",
        },
      ],
      phoneNumberLength: { min: 9, max: 10 },
      phoneNumberPrefix: "30",
    }
  ),
};
